import Big from 'big.js'

import { enforceMinimumPrecision } from '@sequencehq/utils'

import { DEFAULT_CREDIT_NOTE_REVENUE_RECOGNITION_METHOD } from 'CreditNotes/constants'
import { AppliedCreditNote, ServerLineItem } from 'Invoices/types'

import { CreditNoteLineItemRow } from './CreditNoteLineItemTable'
import { remainingNetByLineItem } from './remainingNetByLineItem'

/**
 * Builds the drawer's initial rows from the invoice's line items, pre-filling
 * the rev-rec defaults: the service period comes from the referenced invoice
 * line item, falling back to the invoice billing period when it has none, and
 * the recognition method defaults to straight line.
 *
 * Line items already (partially) credited by previously applied credit notes
 * seed with the remaining creditable amount instead of their full value, so a
 * subsequent credit note opens issuable without manual correction. Fully
 * credited line items seed unchecked at zero.
 *
 * When `startUnchecked` is set (a partially-paid invoice), every row seeds
 * unchecked so the merchant picks what to credit — a payment has already
 * consumed part of the balance, so pre-selecting everything would open the
 * drawer over the outstanding amount. Amounts are still seeded, so ticking a
 * row credits that line.
 *
 * A BURNDOWN line item is a V1 credit grant drawn down here: negative, never
 * creditable. It is dropped, and its presence unchecks every row, since the
 * survivors sum above the balance the grant already reduced.
 *
 *   charge 1429.00 + burndown -1500.00 -> balance 214.80
 *   drop burndown -> rows sum 1714.80 > 214.80, so ticked would be blocked
 */
export const seedRows = (
  lineItems: ServerLineItem[],
  billingPeriod: { start: string; endInclusive: string } | undefined,
  appliedCreditNotes: AppliedCreditNote[],
  startUnchecked = false
): CreditNoteLineItemRow[] => {
  const remainingNet = remainingNetByLineItem(lineItems, appliedCreditNotes)

  // Classification alone, deliberately not the server's isCashCredit()
  // (BURNDOWN && title === 'Credit'), which would let usage-based
  // `Credit: {grant name}` rows through.
  const creditableLineItems = lineItems.filter(
    lineItem => lineItem.revenueClassification !== 'BURNDOWN'
  )
  const hasBurndown = creditableLineItems.length < lineItems.length

  return creditableLineItems.map(lineItem => {
    const amountFields = seedAmountFields(
      lineItem,
      remainingNet.get(lineItem.id)
    )

    return {
      rowId: lineItem.id,
      lineItemId: lineItem.id,
      productId: lineItem.productId,
      groupId: lineItem.groupId ?? '',
      title: lineItem.title,
      taxRate: lineItem.taxRate,
      ...amountFields,
      checked: amountFields.checked && !startUnchecked && !hasBurndown,
      servicePeriod: lineItem.servicePeriod
        ? {
            start: lineItem.servicePeriod.startDate,
            endInclusive: lineItem.servicePeriod.endDate
          }
        : billingPeriod,
      revenueRecognitionMethod: DEFAULT_CREDIT_NOTE_REVENUE_RECOGNITION_METHOD
    }
  })
}

/**
 * Amount fields for a seeded row, based on how much of the line item previous
 * credit notes already credited. A reduced amount is expressed as rate ×
 * quantity 1 (the handleChangeTotal convention) because the API recomputes the
 * net from rate × quantity.
 */
const seedAmountFields = (
  lineItem: ServerLineItem,
  remainingNet: Big | undefined
): Pick<CreditNoteLineItemRow, 'checked' | 'qty' | 'rate' | 'price'> => {
  const fullyCreditable =
    remainingNet === undefined || remainingNet.gte(lineItem.netTotal)

  if (fullyCreditable) {
    return {
      checked: true,
      qty: lineItem.quantity,
      rate: lineItem.rate,
      price: enforceMinimumPrecision(2)(lineItem.netTotal)
    }
  }

  if (remainingNet.lte(0)) {
    return { checked: false, qty: '1', rate: '0', price: '0.00' }
  }

  return {
    checked: true,
    qty: '1',
    rate: remainingNet.toString(),
    price: enforceMinimumPrecision(2)(remainingNet.toString())
  }
}
